<template>
  <div class="col-12 col-xxl-6 bc-col">
    <div class="bc-card">
      <BudgetCardHeader :summary="summary" @update:scope="onScopeChange" @update:headType="onHeadTypeChange"/>
      <div class="table-responsive">
        <table class="table table-bordered align-middle bc-table bkm-table">
          <thead>
            <tr>
              <th class="text-start">Metric</th>
              <th class="text-end">YTD Budget</th>
              <template v-if="! isItemHead">
                <th class="text-end">YTD Actual</th>
                <th class="text-end">Variance Fav/(Adv)</th>
                <th class="text-end">Variance %</th>
                <th class="text-center">Status</th>
              </template>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in rows" :key="row.key" :class="{ 'bkm-result': row.is_result }">
              <td class="text-start bc-name">{{ row.name }}</td>
              <td class="text-end bc-amount">{{ formatAmount(row.budget) }}</td>
              <template v-if="! isItemHead">
                <td class="text-end bc-amount">{{ formatAmount(row.actual) }}</td>
                <td class="text-end bc-amount" :class="varianceClass(row.variance)">{{ formatAmount(row.variance) }}</td>
                <td class="text-end bc-amount" :class="varianceClass(row.variance)">{{ formatPercent(row.percent) }}</td>
                <td class="text-center"><span class="bkm-status" :class="statusOf(row.variance).class_name">{{ statusOf(row.variance).name }}</span></td>
              </template>
            </tr>
            <tr v-if="! rows.length">
              <td :colspan="isItemHead ? 2 : 6" class="bkm-empty">No approved budget</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import BudgetCardHeader from '@/components/molecule/company/dashboard/dynamic-dashboard/budget/BudgetCardHeader'
import { ITEM_HEAD, formatAmount, formatPercent, headDataOf, roundAmount, statusOf } from '@/components/molecule/company/dashboard/dynamic-dashboard/budget/budgetFigures'

const props = defineProps({
  statisticsItem: Object,
  itemKey: String,
  page: Object
})

const summary = computed(() => props.statisticsItem[props.itemKey].data || {})

const currentScope = ref(null)
const headType = ref(null)

const onScopeChange = (scope) => {
  currentScope.value = scope
}

const onHeadTypeChange = (value) => {
  headType.value = value
}

const isItemHead = computed(() => headType.value === ITEM_HEAD)

const metricRow = (key, name, budget, actual, variance, isResult) => ({
  key: key,
  name: name,
  budget: budget,
  actual: actual,
  variance: roundAmount(variance),
  percent: budget ? variance / Math.abs(budget) * 100 : 0,
  is_result: isResult
})

const rows = computed(() => {
  const head = headDataOf(currentScope.value, headType.value)
  if (! head) return []
  const { budget, actual } = head
  const budgetNet = roundAmount(budget.income - budget.expense)
  const actualNet = roundAmount(actual.income - actual.expense)
  return [
    metricRow('receipts', 'Total Receipts', budget.income, actual.income, actual.income - budget.income, false),
    metricRow('payments', 'Total Payments', budget.expense, actual.expense, budget.expense - actual.expense, false),
    metricRow('net', 'Net Surplus / (Deficit)', budgetNet, actualNet, actualNet - budgetNet, true),
    metricRow('closing', 'Closing Balance', budget.closing, actual.closing, actual.closing - budget.closing, true)
  ]
})

const varianceClass = (value) => {
  if (value < 0) return 'bc-adverse'
  return value > 0 ? 'bc-favourable' : ''
}
</script>

<style scoped src="./budget/budgetCard.css"></style>

<style scoped>
.bkm-table thead th {
  color: #667085;
}
.bkm-table th:first-child {
  min-width: 170px;
}
.bkm-result td {
  background-color: #f8f9fb;
}
.bkm-result .bc-name {
  font-weight: 700;
}
.bkm-result .bc-amount {
  font-weight: 700;
}
.bkm-status {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}
.bkm-status-favourable {
  color: #0f766e;
  background: #e6f6f3;
}
.bkm-status-adverse {
  color: #c53030;
  background: #fdecec;
}
.bkm-status-neutral {
  color: #667085;
  background: #f2f4f7;
}
.bkm-empty {
  padding: 28px 0 !important;
  text-align: center;
  font-size: 12px;
  color: #9a98a3;
}
</style>
